import React from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  SmartToy as SmartToyIcon,
} from '@mui/icons-material';
import { useCoco } from 'context/CocoContext';

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function CocoConversationList() {
  const theme = useTheme();
  const {
    conversations,
    conversationsLoading,
    activeConversationId,
    setActiveConversationId,
    createConversation,
    deleteConversation,
  } = useCoco();

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      {/* Header */}
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <SmartToyIcon sx={{ color: theme.palette.primary.main, mr: 1 }} />
          <Typography variant="body2" sx={{ fontWeight: 700 }}>
            Ask Coco
          </Typography>
        </Box>

        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => createConversation()}
          sx={{ textTransform: 'none' }}
        >
          Start Chat
        </Button>
      </Box>

      <Divider />

      {/* Recent chats */}
      <Box sx={{ px: 2, pt: 1.5 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>
          Recent Chats
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {conversationsLoading && conversations.length === 0 ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={24} />
          </Box>
        ) : conversations.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            textAlign="center"
            sx={{ px: 2, py: 3 }}
          >
            No conversations yet
          </Typography>
        ) : (
          <List dense disablePadding>
            {conversations.map((conv) => {
              const isActive = conv.id === activeConversationId;
              return (
                <Box
                  key={conv.id}
                  sx={{
                    position: 'relative',
                    '&:hover .delete-btn': { opacity: 1 },
                  }}
                >
                  <ListItemButton
                    selected={isActive}
                    onClick={() => setActiveConversationId(conv.id)}
                    sx={{
                      position: 'relative',
                      ...(isActive
                        ? {
                            bgcolor: `${theme.palette.primary.main}15`,
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            '&.Mui-selected': {
                              bgcolor: `${theme.palette.primary.main}15`,
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: `${theme.palette.primary.main}20`,
                            },
                          }
                        : {}),
                    }}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="body2" noWrap sx={{ fontWeight: isActive ? 600 : 400 }}>
                          {conv.title || 'Untitled chat'}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {conv.updated_at ? timeAgo(conv.updated_at) : ''}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                  <IconButton
                    className="delete-btn"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      deleteConversation(conv.id);
                    }}
                    sx={{
                      opacity: 0,
                      position: 'absolute',
                      right: 8,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      transition: 'opacity 0.2s',
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
          </List>
        )}
      </Box>
    </Box>
  );
}
